import { Check as CheckIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Autocomplete, CardContent, Grid, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import DecreaseButton from "components/DecreaseButton";
import IncreaseButton from "components/IncreaseButton";
import { PromptDialog } from "components/PromptDialog";
import StyledCard from "components/StyledCard";
import { StyledCardActions } from "components/StyledCardActions";
import StyledTextField from "components/StyledTextField";
import { StockSync } from "models/StockSync";
import { TechnicianType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";

const PREFIX = "SyncStockPart";

const classes = {
  buttonWrapper: `${PREFIX}-buttonWrapper`,
  error: `${PREFIX}-error`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  width: "100%",

  [`& .${classes.buttonWrapper}`]: {
    marginTop: "10px",
  },

  [`& .${classes.error}`]: {
    color: theme.palette.error.main,
  },
}));

interface SyncStockPartProps {
  stock: StockSync;
  onSyncPart: (data: StockSync) => void;
  onRemovePart: (data: StockSync) => void;
  moveStock?: boolean;
}

export const SyncStockPart: FC<SyncStockPartProps> = (props) => {
  const { stock, onSyncPart, onRemovePart, moveStock } = props;
  const intl = useIntl();
  const { technicians } = useAppSelector((s) => s.cache);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [editPart, setEditPart] = useState<boolean>(false);
  const [tempQty, setTempQty] = useState<number>(stock.sync.quantity ?? 0);
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianType | null>(
    stock.moveToTech ?? null,
  );
  const [qtyError, setQtyError] = useState<string>("");
  const minusButtonDisabled = (moveStock && tempQty === 1) || tempQty === 0;
  const addButtonDisabled = moveStock && tempQty === (stock.part.quantity ?? 0);

  const changeQtyFromInput = useCallback(
    (value: string) => {
      const numericValue = parseInt(value);

      if (moveStock) {
        const maxLimit = stock.part.quantity ?? 0;

        if (numericValue <= maxLimit) {
          setTempQty(numericValue);
          setQtyError("");
        } else {
          if (numericValue > maxLimit) {
            setQtyError(
              intl.formatMessage(
                {
                  id: "part.quantityTooHigh",
                },
                {
                  qty: maxLimit,
                },
              ),
            );
          }
        }
      } else {
        setTempQty(numericValue);
        setQtyError("");
      }
    },
    [intl, moveStock, stock.part.quantity],
  );

  const onSave = useCallback(() => {
    setEditPart(false);
    if (!stock.part.articleNo) return;
    onSyncPart({
      part: stock.part,
      sync: {
        articleNo: stock.part.articleNo,
        moveToTechnicianGuid: moveStock ? selectedTechnician?.guid : null,
        quantity: tempQty,
      },
      moveToTech: selectedTechnician,
    });
  }, [stock.part, onSyncPart, moveStock, selectedTechnician, tempQty]);

  return (
    <StyledGrid item xs={12} sm={6} lg={4}>
      <StyledCard key={stock.part.articleNo}>
        <CardContent sx={{ width: "100%" }}>
          <Typography variant="body1" component="h3" data-testid="UsedPart-Description">
            <FormattedMessage id="general.description" />: {stock.part.description}
          </Typography>

          <Typography color="textSecondary">{stock.part.articleNo}</Typography>
          {!editPart && (
            <Typography variant="body2" component="p">
              <FormattedMessage id="general.quantity" />: {stock.sync.quantity}
            </Typography>
          )}
          {!editPart && moveStock && (
            <Typography variant="body2" component="p">
              <FormattedMessage id="stock.moveTo" />: {stock.moveToTech?.name}
            </Typography>
          )}

          {editPart && (
            <Grid container spacing={2}>
              <Grid item>
                <Typography variant="body2" component="p">
                  <FormattedMessage id="general.quantity" />:
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                className={classes.buttonWrapper}
                wrap="nowrap"
              >
                <Grid item xs={2}>
                  <DecreaseButton
                    disabled={minusButtonDisabled}
                    className={minusButtonDisabled ? "buttonDisabled" : ""}
                    onClick={() => setTempQty(tempQty - 1)}
                  />
                </Grid>
                <Grid item xs={5} textAlign="center">
                  <StyledTextField
                    value={tempQty}
                    id="quantity"
                    type="number"
                    onChange={(event) => changeQtyFromInput(event.target.value)}
                    helperText={qtyError}
                    error={qtyError !== ""}
                    style={{ width: "70%" }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IncreaseButton
                    disabled={addButtonDisabled}
                    className={addButtonDisabled ? "buttonDisabled" : ""}
                    onClick={() => setTempQty(tempQty + 1)}
                  />
                </Grid>
              </Grid>
              {moveStock && (
                <Grid container item>
                  <Autocomplete
                    fullWidth
                    options={technicians}
                    getOptionLabel={(option) => `${option.name}`}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={intl.formatMessage({ id: "stock.selectTechnician" })}
                      />
                    )}
                    value={selectedTechnician}
                    data-testid="MoveStockPart-TechnicianSelect"
                    onChange={(_, value) => {
                      setSelectedTechnician(value);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
        <StyledCardActions>
          <IconButton
            aria-label={intl.formatMessage({ id: "general.delete" })}
            onClick={() => setConfirmationDialog(true)}
            size="large"
          >
            <DeleteIcon color="error" />
          </IconButton>
          {editPart ? (
            <IconButton
              aria-label={intl.formatMessage({ id: "general.save" })}
              onClick={onSave}
              size="large"
            >
              <CheckIcon color="primary" />
            </IconButton>
          ) : (
            <IconButton
              aria-label={intl.formatMessage({ id: "general.edit" })}
              onClick={() => setEditPart(true)}
              size="large"
            >
              <EditIcon color="action" />
            </IconButton>
          )}
        </StyledCardActions>
      </StyledCard>
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={confirmationDialog}
        setOpen={setConfirmationDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={() => onRemovePart(stock)}
        title={<FormattedMessage id="part.remove" />}
        promptContent={<FormattedMessage id="stock.removePrompt" />}
      />
    </StyledGrid>
  );
};
